import { NotificationCount } from "@/components/notification-count/NotificationCount.tsx";
import { SidebarHeader } from "@/components/sidebar/components/SidebarHeader.tsx";
import usePermissions from "@/hooks/use-permissions.hook.ts";
import { useMenuCounters } from "@/queries/settings/use-app.query.ts";
import { ApiPermission } from "@/types/role.types.ts";
import { ApiFeatureType } from "@/types/subscription.types.ts";
import { FontAwesomeIcon, SidebarPanelItem, Stack, Title } from "@skailer-com/uikit";
import { ComponentProps, Fragment } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";

interface SideBarReviewsItem {
  titleGroup?: string;
  children: ComponentProps<typeof SidebarPanelItem>[];
}

export const SidebarReviews = () => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const { canIEdit, canIView, isFeatureEnabled } = usePermissions();
  const { data: menuCounters } = useMenuCounters();

  const isReviewsEnabled =
    isFeatureEnabled(ApiFeatureType.talent_performance_reviews) &&
    canIEdit(ApiPermission.performance_reviews_module);

  const canViewReviewTemplates =
    isReviewsEnabled &&
    canIEdit(ApiPermission.performance_reviews_module) &&
    canIView(ApiPermission.performance_review_templates);

  const requestsCount = menuCounters?.performance_review?.requests.count ?? 0;

  const menu: SideBarReviewsItem[] = [
    {
      children: [
        {
          title: t("common:requests"),
          to: "/my-feedback/requests",
          isActive:
            pathname === "/my-feedback/requests" || pathname.startsWith("/my-feedback/requests"),
          icon: <FontAwesomeIcon icon="fa-light fa-inbox" />,
          subtitleSlot:
            requestsCount > 0 ? <NotificationCount count={requestsCount} size={"sm"} /> : undefined,
        },
      ],
    },
    {
      titleGroup: t("performance_review:manage"),
      children: [
        {
          title: t("performance_review:review_cycles"),
          to: "/performance-reviews",
          isActive:
            pathname === "/performance-reviews" || pathname.startsWith("/performance-reviews"),
        },
        ...(canViewReviewTemplates
          ? [
              {
                title: t("common:templates"),
                to: "/templates/performance-reviews",
                isActive: pathname.startsWith("/templates/performance-reviews"),
              },
            ]
          : []),
      ],
    },
    {
      titleGroup: t("performance_review:my_reviews"),
      children: [
        {
          title: t("performance_review:feedback_about_me"),
          to: "/my-feedback/feedback-about-me",
          isActive:
            pathname === "/my-feedback/feedback-about-me" ||
            pathname.startsWith("/my-feedback/feedback-about-me"),
        },
        {
          title: t("performance_review:feedback_ive_given"),
          to: "/my-feedback/feedbacks-left",
          isActive:
            pathname === "/my-feedback/feedbacks-left" ||
            pathname.startsWith("/my-feedback/feedbacks-left"),
        },
      ],
    },
    {
      titleGroup: t("core:team"),
      children: [
        {
          title: t("core:direct_reports"),
          to: "/my-feedback/my-direct-reports",
          isActive:
            pathname === "/my-feedback/my-direct-reports" ||
            pathname.startsWith("/my-feedback/my-direct-reports"),
        },
        {
          title: t("common:my_team"),
          to: "/my-feedback/my-team",
          isActive:
            pathname === "/my-feedback/my-team" || pathname.startsWith("/my-feedback/my-team"),
        },
      ],
    },
  ];

  return (
    <>
      <SidebarHeader title={t("performance_review:performance_review")} />
      <Stack className={"px-4"} gap={"xs"}>
        {isReviewsEnabled &&
          menu.map(({ children, titleGroup }, index) => (
            <Fragment key={`reviews-group-${index}`}>
              {titleGroup && (
                <Title header={titleGroup} variant="secondary" size="xs" className="ml-2 mt-4" />
              )}
              {children.map((item) => (
                <SidebarPanelItem
                  key={`${item.to}-${item.title}`}
                  to={item.to}
                  isActive={item.isActive}
                  title={item.title}
                  icon={item.icon}
                  count={item.count}
                  subtitleSlot={item.subtitleSlot}
                />
              ))}
            </Fragment>
          ))}
      </Stack>
    </>
  );
};
